// Firmwares — uploaded firmware catalog for IP phones (list / upload / delete).
// Backend: api/routes/firmwares.js, mounted at /api/firmwares (see api/index.js).
//   GET    /api/firmwares          -> list rows (firmware_uuid, vendor, model, version, ...)
//   POST   /api/firmwares          -> multipart upload; field name "firmware"; body needs vendor+model+version
//   DELETE /api/firmwares/:id      -> delete by firmware_uuid
// Pushing firmware to phones is a SEPARATE concern handled by the Deploy panel
// (POST /api/firmwares/deploy, #1049/#1057) — not from this catalog page.

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Search, Trash2 } from 'lucide-react';
import { getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function PhoneFirmwares() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');

  const { data: rows = [], isError: listError } = useQuery<any[]>({
    queryKey: ['phone-firmwares'],
    queryFn: () => fJson('/api/firmwares'),
    refetchInterval: 30_000,
  });

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return (rows as any[]).filter((r: any) =>
      [r.file_path, r.version, r.vendor, r.model].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [rows, search]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // The backend requires vendor + model + version metadata alongside the file.
      const vendor = window.prompt('Vendor (e.g. grandstream, yealink):')?.trim();
      if (!vendor) return;
      const model = window.prompt('Model (e.g. GRP2615):')?.trim();
      if (!model) return;
      const version = window.prompt('Firmware version (e.g. 1.0.5.35):')?.trim();
      if (!version) return;
      const fd = new FormData();
      fd.append('firmware', file);
      fd.append('vendor', vendor);
      fd.append('model', model);
      fd.append('version', version);
      const r = await fetch('/api/firmwares', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['phone-firmwares'] }),
    onError: (e: any) => alert(`Upload failed: ${e.message}`),
  });

  const remove = useMutation({
    mutationFn: (id: string) => fJson(`/api/firmwares/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['phone-firmwares'] }),
    onError: (e: any) => alert(`Delete failed: ${e.message}`),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/phones')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Phones</button>
        <h1 className="text-xl font-semibold">Firmwares</h1>
      </div>

      {listError && <div className="card p-3 text-sm text-danger bg-danger/10">Could not load firmware catalog — API unavailable.</div>}

      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
          <Plus size={14} /> {upload.isPending ? 'Uploading…' : 'Add'}
        </button>
        <input ref={fileInput} type="file" className="hidden" accept=".bin,.fw,.rom,.zip"
               onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />

        <div className="flex-1" />

        <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1">
          <Search size={14} className="text-textSecondary" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                 className="bg-transparent outline-none text-sm w-48" />
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>File</th>
              <th>Version</th>
              <th>Vendor</th>
              <th>Model</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r: any) => (
              <tr key={r.firmware_uuid} className="hover:bg-elevated">
                <td className="font-mono text-xs">{(r.file_path ? r.file_path.split('/').pop() : null) || r.version || '—'}</td>
                <td className="font-mono text-xs">{r.version || '—'}</td>
                <td>{r.vendor || '—'}</td>
                <td>{r.model || '—'}</td>
                <td>
                  <button className="btn-ghost p-1 text-danger" title="Delete"
                          onClick={() => confirm('Delete firmware file?') && remove.mutate(r.firmware_uuid)}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-textSecondary">
                No firmware files uploaded. Click <strong>Add</strong> to upload a .bin / .fw / .rom file.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
